import React, { useEffect, useState, useContext } from 'react';
import { AuthContext } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { API_BASE } from '../lib/config';

const API = `${API_BASE}`;

const yen = (n) => new Intl.NumberFormat('ja-JP').format(n || 0);

/* Enhanced State for Payment Safety and Feedback */
const EmployerPayments = () => {
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);

  const [amount, setAmount] = useState('');
  const [quote, setQuote] = useState(null);
  const [loading, setLoading] = useState(false);
  const [idempotencyKey, setIdempotencyKey] = useState('');
  const [paymentError, setPaymentError] = useState(null); // { title: string, message: string, canRetry: boolean }
  const [processing, setProcessing] = useState(false); // Distinct from 'loading' for specificity
  const [workerId, setWorkerId] = useState('');
  const [pmId, setPmId] = useState('');
  const [desc, setDesc] = useState('');

  useEffect(() => {
    // ... auth checks ...
    if (!user || user.user_type !== 'employer') {
      toast.error('事業者権限が必要です');
      navigate('/');
    }
  }, [user, navigate]);

  const generateIdemKey = () => `pay-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;

  const fetchQuote = async () => {
    setPaymentError(null);
    const base = parseInt(amount, 10);
    if (!base || base <= 0) {
      toast.error('金額を入力してください');
      return;
    }
    try {
      setLoading(true);
      const res = await fetch(`${API}/employers/payments/quote`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
        },
        body: JSON.stringify({ amount: base })
      });
      if (!res.ok) throw new Error('見積取得に失敗しました');
      const data = await res.json();
      setQuote(data);
      // Generate Key on new quote
      setIdempotencyKey(generateIdemKey());
    } catch (e) {
      toast.error(e.message || '見積取得に失敗しました');
    } finally {
      setLoading(false);
    }
  };

  const doCharge = async () => {
    if (!quote) {
      toast.error('先に見積を取得してください');
      return;
    }

    setProcessing(true);
    setPaymentError(null);

    // Safety check: key must exist
    let currentKey = idempotencyKey;
    if (!currentKey) {
      currentKey = generateIdemKey();
      setIdempotencyKey(currentKey);
    }

    try {
      const res = await fetch(`${API}/employers/payments/charge`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
        },
        body: JSON.stringify({
          amount: quote.amount,
          payment_method_id: pmId || undefined,
          description: desc || undefined,
          worker_id: workerId || undefined,
          idempotency_key: currentKey
        })
      });

      const data = await res.json();

      if (!res.ok) {
        // Handle specifics
        if (res.status === 402) {
          // Card Error - Safe to retry with NEW key (user likely changes card or tries again)
          // But wait, if they try 'same' card, maybe same key is faster? 
          // Usually decline means we should get fresh state.
          // Also, we MUST show "Payment Failed" instantly.
          setPaymentError({
            title: '決済できませんでした',
            message: `${data.detail || 'カード決済に失敗しました'}。カード情報をご確認の上、もう一度お試しください。`,
            canRetry: true
          });
          // Prepare new key for next attempt automatically
          setIdempotencyKey(generateIdemKey());
        } else {
          // 500 or others - Might be stuck. 
          // Keep key if it was a network glitch? 
          // For safety, warn user.
          setPaymentError({
            title: '決済エラー',
            message: data.detail || 'システムエラーが発生しました。時間をおいて再試行してください。',
            canRetry: true
          });
          // We generally keep the key for 500s to avoid double charge if it's actually processing?
          // But if user wants to force retry, they might get same error.
        }
        throw new Error(data.detail || '決済に失敗しました');
      }

      // Success
      toast.success('決済が完了しました');
      navigate('/employer-dashboard');

    } catch (e) {
      console.error(e);
      // Already handled in setPaymentError for logic, just stop loading
      if (!paymentError) {
        // fallback for network errors or unexpected exceptions
        setPaymentError({
          title: '通信エラー',
          message: '決済サーバーと通信できませんでした。通信環境を確認し、「この内容で支払う」ボタンをもう一度押してください（二重決済防止機能が働いています）。',
          canRetry: true
        });
      }
    } finally {
      setProcessing(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="container mx-auto px-4 py-8 max-w-3xl">
        <div className="mb-6 flex items-center">
          <button onClick={() => navigate('/employer-dashboard')} className="text-gray-600 hover:text-gray-900 mr-3">← ダッシュボードに戻る</button>
          <h1 className="text-2xl font-bold">報酬支払い</h1>
        </div>

        <div className="bg-white rounded-lg shadow p-6 mb-6">
          <label className="block text-sm font-medium text-gray-700 mb-2">ワーカーへの支払い基準額（円）</label>
          <input type="number" value={amount} onChange={(e) => setAmount(e.target.value)} className="w-full px-3 py-2 border rounded" placeholder="例: 10000" />
          <button onClick={fetchQuote} disabled={loading || processing} className="mt-4 bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700 disabled:opacity-50">見積を取得</button>
        </div>

        {quote && (
          <div className="bg-white rounded-lg shadow p-6 mb-6">
            <h2 className="text-lg font-semibold mb-4">見積内訳</h2>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between"><span>ワーカー受取額</span><span>¥{yen(quote.worker_receive)}</span></div>
              <div className="flex justify-between"><span>運営手数料（{Math.round(quote.platform_fee_rate * 100)}%）</span><span>¥{yen(quote.platform_fee)}</span></div>
              <div className="flex justify-between font-semibold"><span>事業者お支払額</span><span>¥{yen(quote.employer_total)}</span></div>
              <div className="text-gray-500">※ ワーカーの引き出し時に別途 ¥{yen(quote.withdrawal_fee)} がかかります</div>
            </div>
          </div>
        )}

        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-lg font-semibold mb-4">決済</h2>

          {/* Error Display Area */}
          {paymentError && (
            <div className="mb-6 bg-red-50 border-l-4 border-red-500 p-4">
              <div className="flex">
                <div className="flex-shrink-0">
                  <svg className="h-5 w-5 text-red-500" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                  </svg>
                </div>
                <div className="ml-3">
                  <h3 className="text-lg leading-6 font-medium text-red-800">
                    {paymentError.title}
                  </h3>
                  <div className="mt-2 text-sm text-red-700">
                    <p>{paymentError.message}</p>
                    {paymentError.canRetry && (
                      <p className="mt-2 font-bold">
                        → そのまま再度「この内容で支払う」ボタンを押してください。
                      </p>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}

          <label className="block text-sm font-medium text-gray-700 mb-1">ワーカーID（接続アカウントに振替、任意）</label>
          <input type="text" value={workerId} onChange={(e) => setWorkerId(e.target.value)} className="w-full px-3 py-2 border rounded mb-3" placeholder="worker_id（任意）" />
          <label className="block text-sm font-medium text-gray-700 mb-1">Stripe PaymentMethod ID（任意）</label>
          <input type="text" value={pmId} onChange={(e) => setPmId(e.target.value)} className="w-full px-3 py-2 border rounded mb-3" placeholder="pm_xxx（未入力でも可）" />
          <label className="block text-sm font-medium text-gray-700 mb-1">説明（任意）</label>
          <input type="text" value={desc} onChange={(e) => setDesc(e.target.value)} className="w-full px-3 py-2 border rounded mb-4" placeholder="メモなど" />

          <button
            onClick={doCharge}
            disabled={loading || !quote || processing}
            className={`w-full text-white px-6 py-3 rounded-lg font-bold text-lg shadow-md transition-all ${processing
              ? 'bg-gray-400 cursor-wait'
              : 'bg-green-600 hover:bg-green-700 hover:shadow-lg'
              }`}
          >
            {processing ? (
              <span className="flex items-center justify-center">
                <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                決済処理中...
              </span>
            ) : 'この内容で支払う'}
          </button>

          <p className="mt-4 text-xs text-gray-500 text-center">
            ※ 「この内容で支払う」ボタンは一度だけ押してください。二重決済防止機能が有効です。
          </p>
        </div>
      </div>
    </div>
  );
};

export default EmployerPayments;
